import { Container, Box, Typography, Button, Grid } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router";
import hero_img from "../assets/mining-platform-header.jpg";
import directoryImage from "../assets/mining-companies-directory.png";
import rankingsImage from "../assets/mining-companies-rankings.png";
import screenerImage from "../assets/mining-companies-screener.png";

const SECTION_PY = { xs: 8, md: 11 };
const SECTION_PY_TIGHT = { xs: 6, md: 9 };
const CONTAINER_MAX = "lg";
const TEXT_MAX = 780;
const CARD_RADIUS = 4;
const CARD_PX = 3.25;
const CARD_PY = 4;
const GRID_GAP = { xs: 3, md: 4 };

const kickerSx = { letterSpacing: 1.2, color: "primary.main", mb: 1.5, textTransform: "uppercase" };
const titleSx = {
  fontWeight: 800,
  lineHeight: 1.14,
  letterSpacing: "-0.3px",
  mb: 3,
  fontSize: { xs: "1.85rem", md: "2.3rem" },
};
const titleDividerSx = { width: 48, height: 2, backgroundColor: "primary.main", mt: 1.5, mb: 3 };
const bodySx = {
  color: "text.secondary",
  lineHeight: 1.75,
  fontSize: { xs: "1.1rem", md: "1.155rem" },
};
const cardTitleSx = { fontWeight: 700, mb: 1 };
const cardBodySx = { color: "text.secondary", lineHeight: 1.7, fontSize: "0.9625rem" };
const productButtonSx = {
  alignSelf: "center",
  fontWeight: 700,
  minWidth: 180,
  py: 1.35,
  fontSize: "1rem",
};

const bulletDotSx = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: "primary.main",
  opacity: 0.75,
  mt: "0.5rem",
  flexShrink: 0,
};

const cardSx = (theme, options = {}) => {
  const {
    align = "left",
    borderAlpha = 0.12,
    hoverBorderAlpha = 0.18,
    shadow = 1,
    hoverShadow = 3,
    highlight = false,
  } = options;
  return {
    p: { xs: "36px", md: "42px" },
    borderRadius: CARD_RADIUS,
    border: `1px solid ${alpha(theme.palette.text.primary, borderAlpha)}`,
    borderTop: highlight ? `3px solid ${theme.palette.primary.main}` : undefined,
    boxShadow: theme.shadows[highlight ? Math.max(shadow, 3) : shadow],
    backgroundColor: theme.palette.common.white,
    transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    height: "100%",
    width: "100%",
    textAlign: align,
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[hoverShadow],
      borderColor: alpha(theme.palette.text.primary, hoverBorderAlpha),
    },
  };
};

const productCards = [
  {
    title: "Browse Company Directory",
    text: "Search mining companies by profile, lifecycle stage, commodity, jurisdiction, assets, and key metrics.",
    button: "Browse Companies",
    path: "/companies",
  },
  {
    title: "Explore Curated Rankings",
    text: "Discover ideas through prebuilt rankings by commodity, jurisdiction, lifecycle stage, performance, and asset characteristics.",
    button: "Explore Rankings",
    path: "/rankings",
  },
  {
    title: "Build Custom Screens",
    text: "Filter and score companies across financials, resources, jurisdictions, commodities, lifecycle stage, and asset intelligence.",
    button: "Open Screener",
    path: "/screener",
    highlight: true,
  },
];

const dataStats = [
  { value: "1,300+", label: "Mining Companies" },
  { value: "7,700+", label: "Mining Assets" },
  { value: "50+", label: "Jurisdictions" },
  { value: "200+", label: "Financial Metrics" },
  { value: "100+", label: "Screening Filters" },
];

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <Box>
      <Box
        sx={{
          backgroundImage: `url(${hero_img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: 20,
          px: 3,
          color: "white",
          textAlign: "center",
          position: "relative",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography variant="h2" fontWeight={800}>
            The Intelligence Layer for Global Mining Equities
          </Typography>
          <Typography variant="h5" sx={{ mt: "36px", maxWidth: 860, mx: "auto" }}>
            The fastest way to discover, compare, and evaluate mining companies.
          </Typography>
          <Box sx={{ mt: "52px", display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="large"
              sx={{ px: 5, py: 1.6, fontWeight: 700 }}
              onClick={() => navigate("/rankings")}
            >
              Explore Rankings
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 5,
                py: 1.6,
                fontWeight: 700,
                color: "white",
                borderColor: "rgba(255,255,255,0.8)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
              onClick={() => navigate("/screener")}
            >
              Open Screener
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: (theme) =>
              `linear-gradient(180deg, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.34) 65%, ${alpha(
                theme.palette.background.default,
                0.9
              )} 100%)`,
          }}
        />
      </Box>

      <Box id="data" sx={{ backgroundColor: "background.default" }}>
        <Container maxWidth={CONTAINER_MAX} sx={{ pt: { xs: "68px", md: "92px" }, pb: { xs: "28px", md: "52px" } }}>
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography variant="overline" sx={kickerSx}>
              THE DATASET
            </Typography>
            <Typography variant="h3" component="h2" sx={titleSx}>
              Every Company. Every Asset. One Standardized Framework.
            </Typography>
            <Box sx={{ ...titleDividerSx, mx: "auto" }} />
            <Typography variant="body1" sx={{ ...bodySx, maxWidth: 860, mx: "auto" }}>
              ResourceNAV transforms fragmented company disclosures, technical reports, and financial statements into a standardized framework for mining investment research.
            </Typography>
          </Box>
          <Box
            sx={(theme) => ({
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(5, minmax(0, 1fr))" },
              rowGap: { xs: 3, sm: 0 },
              maxWidth: 1040,
              mx: "auto",
              py: { xs: 1, md: 1.5 },
              "& > *": {
                borderRight: { xs: "none", sm: `1px solid ${alpha(theme.palette.text.primary, 0.119)}` },
              },
              "& > *:nth-of-type(2n)": {
                borderRight: { xs: "none", sm: `1px solid ${alpha(theme.palette.text.primary, 0.119)}` },
              },
              "& > *:last-of-type": {
                borderRight: "none",
              },
            })}
          >
            {dataStats.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: { xs: 1.5, md: 3 },
                    minHeight: { xs: 92, sm: 104 },
                  }}
                >
                  <Typography
                    variant="h4"
                    fontWeight={900}
                    color="primary.main"
                    sx={{ lineHeight: 1, fontSize: { xs: "2.2rem", md: "2.45rem" } }}
                  >
                    {item.value}
                  </Typography>
                  <Typography variant="body2" fontWeight={800} color="text.secondary" sx={{ mt: "13px", lineHeight: 1.25 }}>
                    {item.label}
                  </Typography>
                </Box>
            ))}
          </Box>
          <Typography
            variant="body1"
            sx={{ ...bodySx, maxWidth: 760, mx: "auto", mt: { xs: 3, md: 4 }, textAlign: "center" }}
          >
            Designed to make mining companies comparable across every stage of the investment process.
          </Typography>
        </Container>
      </Box>

      <Box id="products">
        <Box sx={{ px: { xs: 2, md: 3 }, py: SECTION_PY }}>
          <Box
            sx={{
              background: (theme) =>
                `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(
                  theme.palette.common.white,
                  0.44
                )})`,
              borderRadius: { xs: 5, md: 8 },
              px: { xs: 2.5, md: 4 },
              py: { xs: 4.5, md: 8 },
              mx: "auto",
              maxWidth: { xs: "100%", md: 1260 },
              boxShadow: (theme) => `0 24px 60px ${alpha(theme.palette.common.black, 0.08)}`,
            }}
          >
            <Container maxWidth="md">
              <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant="overline" sx={kickerSx}>
                  EXPLORE THE PLATFORM
                </Typography>
                <Typography variant="h3" component="h2" sx={titleSx}>
                  Three Ways to Use ResourceNAV
                </Typography>
                <Box sx={{ ...titleDividerSx, mx: "auto" }} />
                <Typography variant="body1" sx={{ ...bodySx, maxWidth: TEXT_MAX, mx: "auto" }}>
                  Choose the workflow that matches your research process, from company discovery to custom investment screening.
                </Typography>
              </Box>
            </Container>
            <Container maxWidth="lg">
              <Grid container spacing={GRID_GAP} alignItems="stretch">
                {productCards.map((item) => (
                  <Grid size={{ xs: 12, md: 4 }} key={item.title} sx={{ display: "flex" }}>
                    <Box
                      sx={(theme) =>
                        cardSx(theme, {
                          highlight: item.highlight,
                          shadow: item.highlight ? 2 : 1,
                          hoverShadow: item.highlight ? 5 : 3,
                        })
                      }
                    >
                      <Typography variant="h5" component="h3" sx={{ ...cardTitleSx, mb: 2 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ ...cardBodySx, mb: 4 }}>
                        {item.text}
                      </Typography>
                      <Button
                        variant={item.highlight ? "contained" : "outlined"}
                        onClick={() => navigate(item.path)}
                        sx={{ mt: "auto", ...productButtonSx }}
                      >
                        {item.button}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        </Box>
      </Box>

      <Box id="showcase">
        <Container maxWidth={CONTAINER_MAX} sx={{ pt: { xs: 9, md: 13 }, pb: SECTION_PY }}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography variant="overline" sx={kickerSx}>
              THE PRODUCT
            </Typography>
            <Typography variant="h3" component="h2" sx={titleSx}>
              Explore the Platform in Action
            </Typography>
            <Box sx={{ ...titleDividerSx, mx: "auto" }} />
            <Typography variant="body1" sx={{ ...bodySx, maxWidth: TEXT_MAX, mx: "auto" }}>
              Powerful workflows designed to help investors discover opportunities, compare companies, and build conviction.
            </Typography>
          </Box>
          <Grid container spacing={GRID_GAP}>
            {[
              {
                title: "Company Directory",
                text: "Start with a global mining universe and drill into standardized company profiles, assets, jurisdictions, and financial context.",
                button: "Browse Companies",
                path: "/companies",
                image: directoryImage,
              },
              {
                title: "Curated Rankings",
                text: "Explore prebuilt rankings designed for idea generation across commodities, jurisdictions, lifecycle stages, and performance metrics.",
                button: "Explore Rankings",
                path: "/rankings",
                image: rankingsImage,
              },
              {
                title: "Screener",
                text: "Build thesis-driven company universes using advanced filters, scoring, financial metrics, and asset-level intelligence.",
                button: "Open Screener",
                path: "/screener",
                image: screenerImage,
              },
            ].map((item, idx) => (
              <Grid size={{ xs: 12 }} key={item.title}>
                <Grid container columnSpacing="50px" rowSpacing={3} alignItems="center" direction={idx % 2 ? "row-reverse" : "row"}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={(theme) => ({
                        minHeight: { xs: 260, md: 360 },
                        borderRadius: CARD_RADIUS,
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                        background:
                          idx === 2
                            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)}, ${alpha(theme.palette.secondary.main, 0.14)})`
                            : `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.06)}, ${alpha(theme.palette.primary.main, 0.12)})`,
                        boxShadow: theme.shadows[2],
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        px: "8px",
                        pt: "20px",
                        pb: "10px",
                        overflow: "hidden",
                      })}
                    >
                      <Box
                        component="img"
                        src={item.image}
                        alt={`${item.title} screenshot`}
                        sx={(theme) => ({
                          width: "100%",
                          maxWidth: 520,
                          aspectRatio: "16 / 10",
                          borderRadius: 2,
                          backgroundColor: "rgba(255,255,255,0.72)",
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                          boxShadow: `0 18px 50px ${alpha(theme.palette.common.black, 0.1)}`,
                          display: "block",
                          objectFit: "cover",
                          objectPosition: "top left",
                          overflow: "hidden",
                        })}
                      />
                      <Typography variant="subtitle1" fontWeight={800} color="text.secondary" sx={{ mt: 2 }}>
                        {item.title}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h4" component="h3" sx={{ fontWeight: 800, mb: 1.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body1" sx={{ ...bodySx, mb: 3 }}>
                      {item.text}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant={idx === 2 ? "contained" : "outlined"}
                        onClick={() => navigate(item.path)}
                        sx={productButtonSx}
                      >
                        {item.button}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="platform">
        <Box sx={{ px: { xs: 2, md: 3 }, py: SECTION_PY_TIGHT }}>
          <Box
            sx={{
              background: (theme) =>
                `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.72)}, ${alpha(
                  theme.palette.common.white,
                  0.36
                )})`,
              borderRadius: { xs: 5, md: 8 },
              px: { xs: 2.5, md: 4 },
              py: { xs: 4.5, md: 8 },
              mx: "auto",
              maxWidth: { xs: "100%", md: 1260 },
              boxShadow: (theme) => `0 24px 60px ${alpha(theme.palette.common.black, 0.08)}`,
            }}
          >
            <Container maxWidth="md">
              <Box sx={{ textAlign: "center", mb: 5 }}>
                <Typography variant="overline" sx={kickerSx}>
                  THE FOUNDATION
                </Typography>
                <Typography variant="h3" component="h2" sx={titleSx}>
                  Built on Standardized Mining Intelligence
                </Typography>
                <Box sx={{ ...titleDividerSx, mx: "auto" }} />
                <Typography variant="body1" sx={{ ...bodySx, maxWidth: 820, mx: "auto" }}>
                  Every company is modeled using a consistent framework so mining companies can be compared objectively across financial, operational, and geological dimensions.
                </Typography>
              </Box>
            </Container>
            <Container maxWidth="lg">
              <Grid container spacing={GRID_GAP} alignItems="stretch">
                {[
                  {
                    title: "Standardized Data",
                    text: "Every company is modeled using a consistent framework.",
                  },
                  {
                    title: "Objective Comparison",
                    text: "Compare companies across commodities, lifecycle stages, jurisdictions, financials, and assets.",
                  },
                  {
                    title: "Continuously Updated",
                    text: "ResourceNAV evolves with new filings, technical reports, financials, and market data.",
                  },
                ].map((item, idx) => (
                  <Grid size={{ xs: 12, md: 4 }} key={item.title} sx={{ display: "flex" }}>
                    <Box
                      sx={(theme) =>
                        cardSx(theme, {
                          align: "center",
                          borderAlpha: 0.1,
                          hoverBorderAlpha: 0.14,
                          shadow: 0,
                          hoverShadow: 2,
                          highlight: idx === 0,
                        })
                      }
                    >
                      <Typography variant="h6" component="h3" sx={{ ...cardTitleSx, mb: 1.5 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={cardBodySx}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        </Box>
      </Box>

      <Box id="vision">
        <Container maxWidth={false} sx={{ maxWidth: 1260, py: SECTION_PY_TIGHT }}>
          <Box
            sx={{
              p: { xs: 4.5, md: 6.5 },
              borderRadius: CARD_RADIUS,
              border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(
                  theme.palette.secondary.light,
                  0.18
                )})`,
              backdropFilter: "blur(6px)",
              boxShadow: (theme) => `0 24px 70px ${alpha(theme.palette.primary.main, 0.16)}`,
            }}
          >
            <Typography variant="overline" sx={kickerSx}>
              THE VISION
            </Typography>
            <Typography variant="h3" component="h2" sx={titleSx}>
              Why ResourceNAV Exists
            </Typography>
            <Box sx={{ ...titleDividerSx, mx: 0 }} />
            <Typography variant="body1" sx={{ ...bodySx, maxWidth: 860 }}>
              Mining companies shouldn't be compared using spreadsheets, PDFs, and inconsistent disclosures. ResourceNAV creates a standardized analytical foundation that makes objective comparison possible.
            </Typography>
            <Grid container spacing={GRID_GAP} sx={{ mt: 4 }}>
              {[
                "Mining data speaks a common language",
                "Decisions are grounded in structured context",
                "Comparisons are transparent and objective",
                "Insights emerge from signal, not noise",
              ].map((item) => (
                <Grid size={{ xs: 12, sm: 6 }} key={item}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box sx={bulletDotSx} />
                    <Typography variant="body1" fontWeight={600}>
                      {item}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Box
        id="get-started"
        sx={{
          mt: "20px",
          py: SECTION_PY_TIGHT,
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(
              theme.palette.secondary.main,
              0.16
            )})`,
          borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
        }}
      >
        <Container maxWidth={CONTAINER_MAX} sx={{ textAlign: "center", py: "35px" }}>
          <Typography variant="h3" component="h2" sx={titleSx}>
            Discover Your Next Mining Investment Opportunity
          </Typography>
          <Box sx={{ ...titleDividerSx, mx: "auto" }} />
          <Typography variant="body1" sx={{ ...bodySx, maxWidth: 720, mx: "auto" }}>
            Explore curated rankings, compare companies, and build powerful mining equity screens.
          </Typography>
          <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="large"
              sx={{ px: 5, py: 1.6, fontWeight: 700, minWidth: 220 }}
              onClick={() => navigate("/rankings")}
            >
              Explore Rankings
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ px: 5, py: 1.6, fontWeight: 700, minWidth: 220 }}
              onClick={() => navigate("/screener")}
            >
              Open Screener
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
